import React, { useEffect, useState } from "react";
import CustomTable from "../../../shared/custom-table/custom-table";
import { D17, MobilePay, Poste } from "../../../assets/images";
import {
  columnsRequestsCurrentStudent,
  columnsRequestsCurrentTeacher,
} from "../../../mocks/fakeData";
import { getAllStudentCurrentRequests } from "../../../services/student-offer";
import { RootState } from "../../../redux/store/store";
import { useSelector } from "react-redux";
import { getCurrentTeacherRequests } from "../../../services/teacher-offer";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUniversity, faWallet, faMapMarkerAlt, faClock, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const getCurrentRequestPriority = (status?: string) => {
  if (status?.toUpperCase() === "PENDING") {
    return 2;
  }
  if (status?.toUpperCase() === "ACCEPTED") {
    return 1;
  }
  return 0;
};

const dedupeCurrentRequestsByOffer = (requests: any[]) => {
  const map = new Map<number | string, any>();

  requests.forEach((request: any) => {
    const offerId = request?.studentOffer?.id ?? request?.studentOfferId ?? request?.id;
    if (!offerId) {
      return;
    }

    const current = map.get(offerId);
    if (!current) {
      map.set(offerId, request);
      return;
    }

    const currentPriority = getCurrentRequestPriority(current.status);
    const candidatePriority = getCurrentRequestPriority(request.status);

    if (candidatePriority > currentPriority) {
      map.set(offerId, request);
      return;
    }

    if (candidatePriority === currentPriority) {
      const currentDate = new Date(current.requestDate ?? current.createdAt ?? 0).getTime();
      const candidateDate = new Date(request.requestDate ?? request.createdAt ?? 0).getTime();

      if (candidateDate > currentDate) {
        map.set(offerId, request);
      }
    }
  });

  return Array.from(map.values());
};

const Subscription = () => {
  const role = useSelector(
    (state: RootState) => state?.user?.userData?.role.name,
  );
  const [data, setData] = useState<any>([]);

  useEffect(() => {
    if (role === "ROLE_STUDENT") {
      getAllStudentCurrentRequests()
        .then((res) => {
          const payload = Array.isArray(res?.data) ? res.data : [];
          const deduped = dedupeCurrentRequestsByOffer(payload);

          const normalized = deduped.map((item: any) => {
            return {
              ...item,
              endDate: item.status === "PENDING" ? "N/A" : item.endDate,
            };
          });

          setData(normalized);
        })
        .catch((e) => {
          console.log(e);
        });
    } else {
      getCurrentTeacherRequests()
        .then((res) => {
          setData(res.data);
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, [role]);

  const ActionButtons: React.FC<{ row: any }> = ({ row }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  
    const isValidDate = row.endDate && !isNaN(new Date(row.endDate).getTime());
    const endDate = isValidDate ? new Date(row.endDate) : null;
    
    if (endDate && endDate < today && row.status === 'ACCEPTED') {
      return (
        <span className="px-5 py-2 rounded-xl bg-slate-100 text-slate-400 font-black text-[10px] uppercase tracking-widest border border-slate-200 inline-block">
          Expirée
        </span>
      );
    }

    if (row.status === "ACCEPTED") {
      return (
        <span className="px-5 py-2 rounded-xl bg-emerald-50 text-emerald-600 font-black text-[10px] uppercase tracking-widest border border-emerald-100 inline-block">
          <FontAwesomeIcon icon={faCheck} className="mr-2" /> Acceptée
        </span>
      );
    }

    if (row.status === "REJECTED") {
      return (
        <span className="px-5 py-2 rounded-xl bg-rose-50 text-rose-600 font-black text-[10px] uppercase tracking-widest border border-rose-100 inline-block">
          <FontAwesomeIcon icon={faTimes} className="mr-2" /> Rejetée
        </span>
      );
    }

    return (
      <span className="px-5 py-2 rounded-xl bg-amber-50 text-amber-600 font-black text-[10px] uppercase tracking-widest border border-amber-100 inline-block">
        <FontAwesomeIcon icon={faClock} className="mr-2" /> En attente
      </span>
    );
  };

  const columns = React.useMemo(() => {
    const baseColumns = role === "ROLE_STUDENT" 
      ? [...columnsRequestsCurrentStudent] 
      : [...columnsRequestsCurrentTeacher];
    
    return [
      ...baseColumns,
      {
        Header: "Statut",
        accessor: "status",
        Cell: ({ row }: any) => <ActionButtons row={row.original} />
      }
    ];
  }, [role]);

  const paymentMethods = [
    {
      title: "BNA",
      sub: "Banque Nationale Agricole",
      titulaire: "Omar Riahi",
      rib: "03 200 012 0112 000 266 51",
      icon: MobilePay,
      bg: "bg-gradient-to-br from-[#1a5f7a] to-[#0f3443]",
      color: "text-blue-200"
    },
    {
      title: "La Poste",
      sub: "Compte D17",
      titulaire: "Riahi Omar",
      details: [
        { label: "Numéro D17", val: "4742 0140 4391 9147" },
        { label: "Téléphone D17", val: "22 946 781" }
      ],
      icon: D17,
      bg: "bg-gradient-to-br from-[#f59e0b] to-[#b45309]",
      color: "text-amber-100"
    },
    {
      title: "La Poste",
      sub: "Compte Courant",
      titulaire: "Riahi Omar",
      details: [
        { label: "RIB", val: "17 002000000 3493 484 56" },
        { label: "N° Compte", val: "03 49 34 84 56" }
      ],
      icon: Poste,
      bg: "bg-gradient-to-br from-[#0f3460] to-[#16213e]",
      color: "text-indigo-200"
    },
    {
      title: "Centre intellect",
      sub: "Notre Adresse",
      address: "شارع حسن النوري مقابل المنقالة\nفوق حمادي عبيد\nCentre intellect",
      icon: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
      bg: "bg-gradient-to-br from-[#10b981] to-[#047857]",
      color: "text-emerald-100"
    }
  ];

  return (
    <div className="w-full min-h-screen bg-[#F8FAFB] p-4 lg:p-10 animate-in fade-in duration-700">
      
      {/* Header Section */}
      <div className="mb-12">
        <h1 className="text-3xl font-black leading-none tracking-tight uppercase md:text-4xl text-title">
          Mes <span className="italic text-primary">Abonnements</span>
        </h1>
        <p className="text-[11px] font-bold text-text/40 uppercase tracking-[0.4em] mt-3 ml-1">
          Détails de paiement et historique
        </p>
      </div>

      {/* Payment Methods Grid - Updated to 2 columns on large screens */}
      <div className="grid grid-cols-1 gap-8 mx-auto mb-16 lg:grid-cols-2 max-w-7xl">
        {paymentMethods.map((method, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            whileHover={{ y: -8 }}
            className={`${method.bg} rounded-[3rem] p-8 shadow-2xl relative overflow-hidden group min-h-[320px] flex flex-col justify-between`}
          >
            {/* Background Decoration */}
            <div className="absolute top-0 right-0 w-48 h-48 transition-colors -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/5 blur-3xl group-hover:bg-white/10"></div>
            
            <div className="flex items-start justify-between mb-8">
               <div className="relative z-10">
                  <h3 className="text-4xl font-black leading-none tracking-tighter text-white uppercase">{method.title}</h3>
                  <p className={`text-[11px] font-black ${method.color} uppercase tracking-widest mt-2 opacity-60`}>{method.sub}</p>
               </div>
               <div className="w-20 h-20 bg-white rounded-[2rem] p-4 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-500">
                  <img src={method.icon} alt={method.title} className="object-contain w-full h-full" />
               </div>
            </div>

            <div className="relative z-10 grid grid-cols-1 gap-4 mt-auto sm:grid-cols-2">
               <div className="p-5 border bg-black/10 backdrop-blur-md rounded-2xl border-white/5">
                  <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-1">Titulaire</p>
                  <p className="text-[15px] font-extrabold text-white">{method.titulaire || "Centre Intellect"}</p>
               </div>

               {method.rib && (
                 <div className="p-5 border bg-black/10 backdrop-blur-md rounded-2xl border-white/5 sm:col-span-2">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-1">RIB</p>
                    <p className="text-[13px] font-bold text-white tracking-[0.2em] font-mono">{method.rib}</p>
                 </div>
               )}

               {method.details?.map((d, i) => (
                 <div key={i} className="p-5 border bg-black/10 backdrop-blur-md rounded-2xl border-white/5">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-1">{d.label}</p>
                    <p className="text-[14px] font-bold text-white">{d.val}</p>
                 </div>
               ))}

               {method.address && (
                 <div className="p-5 border bg-black/10 backdrop-blur-md rounded-2xl border-white/5 sm:col-span-2">
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-1 flex items-center gap-2">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-[8px]" /> Localisation
                    </p>
                    <p className="text-[12px] font-bold text-white leading-relaxed text-right dir-rtl whitespace-pre-line mt-2">
                      {method.address}
                    </p>
                 </div>
               )}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="bg-white/80 backdrop-blur-xl rounded-[4rem] p-10 border border-white shadow-2xl relative overflow-hidden max-w-7xl mx-auto">
        <div className="absolute top-0 rounded-full right-10 w-96 h-96 bg-primary/10 blur-3xl -translate-y-1/3"></div>
        <CustomTable
          title="Historique des abonnements"
          columns={columns}
          data={data}
        />
      </div>

      <div className="h-20"></div>
    </div>
  );
};

export default Subscription;
